/**
 * The app's one modal dialog (Apple design review X-8). Every dialog is this shell around its own content,
 * so none of them can differ in what a dialog does to the page:
 *
 * - **Portalled to `<body>`.** Rendered inside the page, a dialog sits in `<main>`'s stacking context
 *   (Layout's `relative z-1`), and the sidebar, the bottom bar and the coach dock paint above its scrim.
 * - **The page behind is inert and still, focus goes in, and focus comes back** to what opened it, once
 *   per opening (`useModal`). Tab stays inside, and Escape and the scrim leave (`useModalKeys`), except
 *   while `locked`: a request in flight must not be cancelled from under itself by a stray key or click.
 * - **The exit plays.** `AnimatePresence` is inside, so a caller passes `open` and never unmounts the dialog
 *   itself; the content keeps its last words while it fades. Each opening mounts the content afresh, so a
 *   form opened twice starts from its own initial values both times.
 * - **The exiting layer catches nothing.** While it fades the host is `pointer-events: none` (`useIsPresent`):
 *   the page is live again the moment the dialog is dismissed, and a tap on it is not eaten by a scrim that
 *   is on its way out.
 *
 * Motion: the panel enters in 220 ms (a 12 px rise and a 0.97 scale with the fade, ease-out) and leaves in
 * 120 ms by fading only, faster than it came, and it stays centred: a modal is not anchored to its trigger.
 * Under Reduce Motion the rise and the scale go and the fades stay.
 *
 * What it does not own is the look: each dialog's panel is drawn by its own artboard, so the caller passes
 * the panel's classes and style. `ConfirmDialog` is the house confirmation built on it.
 */

import { useCallback, useRef, useState, type CSSProperties, type ReactNode, type RefObject } from 'react'
import { createPortal } from 'react-dom'
import { AnimatePresence, motion, useIsPresent, useReducedMotion } from 'motion/react'

import { cn } from '../../lib/utils'
import { useModal, useModalKeys } from './useModal'

const EASE = [0.16, 1, 0.3, 1] as const

/** How long the panel takes to come in and to go, in seconds. The way out is the shorter one. */
export const DIALOG_ENTER_S = 0.22
export const DIALOG_EXIT_S = 0.12

/** The scrim most of the app's artboards draw. The consent screens and the sign-in page pass their own. */
export const DIALOG_SCRIM = 'rgba(28, 24, 22, 0.62)'

/**
 * The ground of a dialog panel drawn on the warm dark: solid. The panels were `TOKENS.warmDark`, 88 % opaque, and
 * at 390 px the session list's row titles showed through the New session panel (measured in the browser). It is
 * the chrome's own solid surface, `--warm-dark` over the gradient's dark end (`fonts.css`).
 */
export const DIALOG_SURFACE = 'var(--chrome-surface)'

interface Props {
  open: boolean
  /** Leaves without an answer: Escape and the scrim. A dialog's own Cancel calls it too. */
  onClose: () => void
  /** `alertdialog` for a choice that ends something; `dialog` for everything else. */
  role?: 'dialog' | 'alertdialog'
  /** The id of the element that names the dialog. Pass this or `label`. */
  labelledBy?: string
  /** The dialog's name, when nothing on it says it. */
  label?: string
  /** The id of the element that describes it, if any. */
  describedBy?: string
  /** Nothing leaves by Escape or the scrim while this is set: a request from inside it is in flight. */
  locked?: boolean
  /** Something stacked above owns the keyboard: Escape and Tab are not this dialog's. */
  paused?: boolean
  /** What takes focus when it opens, if not the panel itself. */
  initialFocus?: RefObject<HTMLElement | null>
  /** What focus goes back to when it leaves, if not whatever had focus when it opened. */
  returnFocus?: RefObject<HTMLElement | null>
  /** Centred (a choice, a short form), or from the top (a long form that scrolls with the page). */
  align?: 'center' | 'top'
  scrim?: string
  /** The panel's width, corner, padding and layout. */
  className?: string
  /** The panel's ground, edge and shadow. */
  style?: CSSProperties
  /** Runs once the dialog has finished leaving. */
  onExitComplete?: () => void
  children: ReactNode
}

export function Dialog({
  open,
  onClose,
  role = 'dialog',
  labelledBy,
  label,
  describedBy,
  locked = false,
  paused = false,
  initialFocus,
  returnFocus,
  align = 'center',
  scrim = DIALOG_SCRIM,
  className,
  style,
  onExitComplete,
  children,
}: Props) {
  const reduce = !!useReducedMotion()
  const hostRef = useRef<HTMLDivElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)

  // Each opening is a new key, so the content mounts afresh even when it opens again while the last one is
  // still fading out (an exiting child that comes back under its own key would keep its old state).
  const [opening, setOpening] = useState(0)
  const [wasOpen, setWasOpen] = useState(open)
  if (open !== wasOpen) {
    setWasOpen(open)
    if (open) setOpening((n) => n + 1)
  }

  useModal(open, hostRef, initialFocus ?? panelRef, returnFocus)

  const leave = useCallback(() => {
    if (!locked) onClose()
  }, [locked, onClose])
  useModalKeys(open && !paused, panelRef, leave)

  return createPortal(
    <AnimatePresence onExitComplete={onExitComplete}>
      {open && (
        <DialogHost key={opening} hostRef={hostRef} align={align}>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1, transition: { duration: 0.18, ease: EASE } }}
            exit={{ opacity: 0, transition: { duration: DIALOG_EXIT_S, ease: EASE } }}
            aria-hidden="true"
            data-testid="dialog-scrim"
            className="fixed inset-0"
            style={{ backgroundColor: scrim }}
            onClick={leave}
          />
          <motion.div
            ref={panelRef}
            role={role}
            aria-modal="true"
            aria-labelledby={labelledBy}
            aria-label={labelledBy ? undefined : label}
            aria-describedby={describedBy}
            tabIndex={-1}
            initial={{ opacity: 0, y: reduce ? 0 : 12, scale: reduce ? 1 : 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1, transition: { duration: DIALOG_ENTER_S, ease: EASE } }}
            exit={{ opacity: 0, transition: { duration: DIALOG_EXIT_S, ease: EASE } }}
            className={cn('relative outline-none', className)}
            style={style}
          >
            {children}
          </motion.div>
        </DialogHost>
      )}
    </AnimatePresence>,
    document.body,
  )
}

/**
 * The fixed layer a dialog lives in, a direct child of `<body>` (what `useModal` leaves un-inert). While it
 * is leaving it lets every pointer through, and it gives up `hostRef`, so a dialog opened again during the
 * fade is the one `useModal` keeps reachable.
 */
function DialogHost({
  hostRef,
  align,
  children,
}: {
  hostRef: RefObject<HTMLDivElement | null>
  align: 'center' | 'top'
  children: ReactNode
}) {
  const present = useIsPresent()
  return (
    <div
      ref={present ? hostRef : undefined}
      data-dialog-leaving={present ? undefined : ''}
      className={cn(
        'fixed inset-0 z-50 flex justify-center p-4',
        align === 'top' ? 'items-start overflow-y-auto sm:p-8' : 'items-center',
        !present && 'pointer-events-none',
      )}
    >
      {children}
    </div>
  )
}
